import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save } from "lucide-react";
import { useMutation, gql } from "@apollo/client";

const UPDATE_PARTICIPANT = gql`
  mutation UpdateParticipantByODRNumber(
    $odrnumber: Int!
    $participant: Int
    $updates: UpdateParticipantInput!
  ) {
    updateParticipantByODRNumber(
      odrnumber: $odrnumber
      participant: $participant
      updates: $updates
    ) {
      message
    }
  }
`;

const normalizeDateInput = (value: string | null | undefined) => {
  if (!value) {
    return "";
  }

  const normalizedValue = String(value);
  if (/^\d+$/.test(normalizedValue)) {
    const parsedFromTimestamp = new Date(Number(normalizedValue));
    if (!Number.isNaN(parsedFromTimestamp.getTime())) {
      return parsedFromTimestamp.toISOString().split("T")[0];
    }
  }

  const parsedDate = new Date(normalizedValue);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.toISOString().split("T")[0];
  }

  return normalizedValue.split("T")[0];
};

const normalizeRso = (value: unknown) => {
  const normalizedValue = String(value ?? "").trim().toLowerCase();
  if (!normalizedValue) {
    return false;
  }

  return ["1", "true", "yes", "y", "rso"].includes(normalizedValue);
};

export const EditLegalGeneral = ({
  isOpen,
  onClose,
  odrNumber,
  participant,
  legalData,
  refetch,
}: {
  isOpen: boolean;
  onClose: () => void;
  odrNumber: number;
  participant: number;
  legalData: any;
  refetch: () => void;
}) => {
	  const [formData, setFormData] = useState({
	    mainNumber: "",
	    arrestDate: "",
	    releasedDate: "",
	    maxDate: "",
	    istStatus: "",
	    istStatusDate: "",
	    commitmentDate: "",
	    completionDate: "",
	    dischargeDate: "",
	    rso: false,
	  });

  const [updateParticipantProgramData, { loading: saving }] = useMutation(UPDATE_PARTICIPANT);

  useEffect(() => {
    setFormData({
	      mainNumber: legalData?.mainNumber || "",
	      arrestDate: normalizeDateInput(legalData?.arrestDate),
	      releasedDate: normalizeDateInput(legalData?.releasedDate),
	      maxDate: normalizeDateInput(legalData?.maxDate),
	      istStatus: legalData?.istStatus?.status || "",
	      istStatusDate: normalizeDateInput(legalData?.istStatus?.statusDate),
	      commitmentDate: normalizeDateInput(legalData?.commitmentDate),
	      completionDate: normalizeDateInput(legalData?.completionDate),
	      dischargeDate: normalizeDateInput(legalData?.dischargeDate),
	      rso: normalizeRso(legalData?.rso),
	    });
  }, [legalData]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === "rso") {
      setFormData((prev) => ({ ...prev, [name]: value === "true" }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const initialMainNumber = legalData?.mainNumber || "";
	    const initialArrestDate = normalizeDateInput(legalData?.arrestDate);
	    const initialReleasedDate = normalizeDateInput(legalData?.releasedDate);
	    const initialMaxDate = normalizeDateInput(legalData?.maxDate);
	    const initialIstStatus = legalData?.istStatus?.status || "";
	    const initialIstStatusDate = normalizeDateInput(legalData?.istStatus?.statusDate);
	    const initialCommitmentDate = normalizeDateInput(legalData?.commitmentDate);
	    const initialCompletionDate = normalizeDateInput(legalData?.completionDate);
	    const initialDischargeDate = normalizeDateInput(legalData?.dischargeDate);
	    const initialRso = normalizeRso(legalData?.rso);

    const updates: Record<string, unknown> = {};
    if (formData.mainNumber.trim() && formData.mainNumber !== initialMainNumber) {
      updates.mainNumber = formData.mainNumber.trim();
    }
    if (formData.arrestDate && formData.arrestDate !== initialArrestDate) {
      updates.arrestDate = formData.arrestDate;
    }
	    if (formData.releasedDate && formData.releasedDate !== initialReleasedDate) {
	      updates.releasedDate = formData.releasedDate;
	    }
	    if (formData.maxDate && formData.maxDate !== initialMaxDate) {
	      updates.maxDate = formData.maxDate;
	    }
	    if (formData.commitmentDate && formData.commitmentDate !== initialCommitmentDate) {
	      updates.commitmentDate = formData.commitmentDate;
	    }
	    if (formData.completionDate && formData.completionDate !== initialCompletionDate) {
	      updates.completionDate = formData.completionDate;
	    }
	    if (formData.dischargeDate && formData.dischargeDate !== initialDischargeDate) {
	      updates.dischargeDate = formData.dischargeDate;
	    }
	    if (
	      (formData.istStatus.trim() && formData.istStatus !== initialIstStatus) ||
	      (formData.istStatusDate && formData.istStatusDate !== initialIstStatusDate)
	    ) {
      updates.istStatus = {
        status: formData.istStatus.trim() || initialIstStatus,
        statusDate: formData.istStatusDate || initialIstStatusDate,
      };
	    }
	    if (formData.rso !== initialRso) {
	      updates.rso = formData.rso ? "1" : "0";
    }

    if (!Object.keys(updates).length) {
      onClose();
      return;
    }

    try {
      await updateParticipantProgramData({
        variables: {
          odrnumber: odrNumber,
          participant,
          updates,
        },
      });
      refetch();
      onClose();
    } catch (error) {
      console.error("Error updating legal info:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden flex flex-col max-h-[90vh]">
        <DialogHeader className="p-4 border-b flex flex-row items-center justify-between bg-white shrink-0">
          <DialogTitle className="text-xl font-bold text-gray-800">
            Edit Legal Information
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/30">
          <form id="edit-legal-general-form" onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              <div className="space-y-2">
                <Label htmlFor="mainNumber" className="text-xs font-bold uppercase text-gray-500">MAIN Last 4</Label>
                <Input
                  id="mainNumber"
                  name="mainNumber"
                  value={formData.mainNumber}
                  onChange={handleInputChange}
                  maxLength={4}
                  className="h-9"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="arrestDate" className="text-xs font-bold uppercase text-gray-500">Arrest Date</Label>
                <Input
                  type="date"
                  id="arrestDate"
                  name="arrestDate"
                  value={formData.arrestDate}
                  onChange={handleInputChange}
                  className="h-9"
                />
              </div>

	              <div className="space-y-2">
	                <Label htmlFor="releasedDate" className="text-xs font-bold uppercase text-gray-500">Projected Release Date</Label>
                <Input
                  type="date"
                  id="releasedDate"
                  name="releasedDate"
                  value={formData.releasedDate}
                  onChange={handleInputChange}
                  className="h-9"
                />
	              </div>

	              <div className="space-y-2">
	                <Label htmlFor="maxDate" className="text-xs font-bold uppercase text-gray-500">Max Date</Label>
	                <Input
	                  type="date"
	                  id="maxDate"
	                  name="maxDate"
	                  value={formData.maxDate}
	                  onChange={handleInputChange}
	                  className="h-9"
	                />
	              </div>

	              <div className="space-y-2">
	                <Label htmlFor="istStatus" className="text-xs font-bold uppercase text-gray-500">IST Status</Label>
	                <Input
	                  id="istStatus"
	                  name="istStatus"
	                  value={formData.istStatus}
	                  onChange={handleInputChange}
	                  className="h-9"
	                />
	              </div>

	              <div className="space-y-2">
	                <Label htmlFor="istStatusDate" className="text-xs font-bold uppercase text-gray-500">IST Status Date</Label>
	                <Input
	                  type="date"
	                  id="istStatusDate"
	                  name="istStatusDate"
	                  value={formData.istStatusDate}
	                  onChange={handleInputChange}
	                  className="h-9"
	                />
	              </div>

	              <div className="space-y-2">
	                <Label htmlFor="commitmentDate" className="text-xs font-bold uppercase text-gray-500">Commitment Date</Label>
	                <Input
	                  type="date"
	                  id="commitmentDate"
	                  name="commitmentDate"
	                  value={formData.commitmentDate}
	                  onChange={handleInputChange}
	                  className="h-9"
	                />
	              </div>

	              <div className="space-y-2">
	                <Label htmlFor="completionDate" className="text-xs font-bold uppercase text-gray-500">Completion Date</Label>
	                <Input
	                  type="date"
	                  id="completionDate"
	                  name="completionDate"
	                  value={formData.completionDate}
	                  onChange={handleInputChange}
	                  className="h-9"
	                />
	              </div>

	              <div className="space-y-2">
	                <Label htmlFor="dischargeDate" className="text-xs font-bold uppercase text-gray-500">Discharge Date</Label>
	                <Input
	                  type="date"
	                  id="dischargeDate"
	                  name="dischargeDate"
	                  value={formData.dischargeDate}
	                  onChange={handleInputChange}
	                  className="h-9"
	                />
	              </div>

	              <div className="space-y-2">
                <Label htmlFor="rso" className="text-xs font-bold uppercase text-gray-500">RSO Status</Label>
                <Select
                  name="rso"
                  value={formData.rso ? "true" : "false"}
                  onValueChange={(value) =>
                    handleInputChange({
                      target: { name: "rso", value },
                    } as any)
                  }
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="false">Not RSO</SelectItem>
                    <SelectItem value="true">RSO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </form>
        </div>

        <div className="flex items-center justify-between p-4 border-t bg-gray-50 shrink-0">
          <Button 
            type="submit" 
            form="edit-legal-general-form" 
            size="sm"
            className="gap-1.5 px-6 shadow-sm"
            disabled={saving}
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
